import { KEY_CODES } from '@cornell-engineering/js/utils/constants';

/**
 * Interactivity for accessible tabs
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Tab_Role
 */
class Tabs {
  constructor(tabsWrapper, orientation = 'horizontal', withHashes = false, onTabChange = null) {
    // Any element containing tablist and tabpanels
    this.tabsWrapper = tabsWrapper;
    this.orientation = orientation;
    this.withHashes = withHashes;
    this.onTabChange = onTabChange;

    this.tabList = this.tabsWrapper.querySelector('[role="tablist"]');
    this.select = this.tabsWrapper.querySelector('select');
    this.tabs = Array.from(this.tabList.querySelectorAll('[role="tab"]'));
    this.tabPanels = Array.from(this.tabsWrapper.querySelectorAll('[role="tabpanel"]'));

    this.tabFocus = 0;
    this.tabMap = {};

    this.init();
  }

  /**
   * Add event listeners
   *
   * @returns void
   */
  init() {
    // Enable arrow navigation between tabs in the tab list
    this.tabList.addEventListener('keydown', e => this.handleKeydown(e));

    // Add a click event handler to each tab
    this.tabs.forEach(tab => {
      tab.addEventListener('click', e => this.changeTabs(e.target));
    });

    if (this.select) {
      this.select.addEventListener('change', e => {
        const selectedTab = this.tabs.find(
          tab => tab.getAttribute('aria-controls') === e.target.value,
        );
        this.changeTabs(selectedTab);
      });
    }

    if (this.withHashes) {
      this.tabs.forEach(tab => {
        const hash = this.getTabHashFromId(tab.id);
        this.tabMap[hash] = tab;
      });

      window.addEventListener('load', () => this.handleHash(), false);
      window.addEventListener('hashchange', () => this.handleHash(), false);
    }
  }

  // Example: 'tab-foo' -> '#foo'
  getTabHashFromId(id) {
    return `#${id.replace('tab-', '')}`.toLowerCase();
  }

  // Programmatically clicks a tab depending on the location hash
  handleHash() {
    const { hash } = window.location;
    if (hash) {
      setTimeout(() => {
        const tabEl = this.tabMap[hash];
        if (tabEl) {
          tabEl.click();
        }
      }, 0);
    }
  }

  /**
   * Increment tab focus index and make active tab focusable
   *
   * @returns void
   */
  incrementTabFocus() {
    this.tabs[this.tabFocus].setAttribute('tabindex', -1);
    this.tabFocus++;
    // If we're at the end, go to the start
    if (this.tabFocus >= this.tabs.length) {
      this.tabFocus = 0;
    }
    this.tabs[this.tabFocus].setAttribute('tabindex', 0);
    this.tabs[this.tabFocus].focus();
  }

  /**
   * Decrement tab focus index and make active tab focusable
   *
   * @returns void
   */
  decrementTabFocus() {
    this.tabs[this.tabFocus].setAttribute('tabindex', -1);
    this.tabFocus--;
    // If we're at the start, move to the end
    if (this.tabFocus < 0) {
      this.tabFocus = this.tabs.length - 1;
    }
    this.tabs[this.tabFocus].setAttribute('tabindex', 0);
    this.tabs[this.tabFocus].focus();
  }

  /**
   * Handle keyboard navigation with arrows
   *
   * @param {KeyboardEvent} e Keyboard event from tablist keyboard event listener
   *
   * @returns void
   */
  handleKeydown(e) {
    switch (e.key) {
      case KEY_CODES.ARROW_LEFT:
      case KEY_CODES.ARROW_LEFT_IE11: {
        e.preventDefault();
        if (this.orientation === 'horizontal') {
          this.decrementTabFocus();
        }
        break;
      }

      case KEY_CODES.ARROW_RIGHT:
      case KEY_CODES.ARROW_RIGHT_IE11: {
        e.preventDefault();
        if (this.orientation === 'horizontal') {
          this.incrementTabFocus();
        }
        break;
      }

      case KEY_CODES.ARROW_UP:
      case KEY_CODES.ARROW_UP_IE11: {
        e.preventDefault();
        if (this.orientation === 'vertical') {
          this.decrementTabFocus();
        }
        break;
      }

      case KEY_CODES.ARROW_DOWN:
      case KEY_CODES.ARROW_DOWN_IE11: {
        e.preventDefault();
        if (this.orientation === 'vertical') {
          this.incrementTabFocus();
        }
        break;
      }

      default: {
        break;
      }
    }
  }

  /**
   * Handle changing tabs when clicking on a tab
   *
   * @param {HTMLElement} target HTML element with role="tab"
   *
   * @returns void
   */
  changeTabs(target) {
    // Remove all current selected tabs
    Array.from(this.tabList.querySelectorAll('[aria-selected="true"]')).forEach(tab =>
      tab.setAttribute('aria-selected', false),
    );

    // Set this tab as selected
    target.setAttribute('aria-selected', true);

    // Keep the select dropdown in sync
    if (this.select) {
      this.select.value = target.getAttribute('aria-controls');
    }

    // Hide all tab panels
    this.tabPanels.forEach(panel => panel.setAttribute('hidden', true));

    // Show the selected panel
    this.tabsWrapper
      .querySelector(`#${target.getAttribute('aria-controls')}`)
      .removeAttribute('hidden');

    // Change URL based on selected tab
    if (this.withHashes && window.history && window.history.pushState) {
      const hash = this.getTabHashFromId(target.id);
      window.history.pushState({}, '', hash);
    }

    // Run an optional function on change
    if (this.onTabChange) {
      this.onTabChange();
    }
  }

  /**
   * Select the first tab to "reset" the tabs
   *
   * @returns void
   */
  resetTabs() {
    const firstTab = this.tabs[0];
    this.changeTabs(firstTab);
  }
}

export default Tabs;
